import Tables from '@/component/Tables';
import PATH from '@/config/PATH';
import { currentDate } from '@/helper/const';
import { getDailyPrizeGuessesAll } from '@/Redux/futures/daily-reward/actions';
import { getBidProduct } from '@/Redux/futures/home/actions';
import { getHistoryBid, getHistoryBidAll } from '@/Redux/futures/rightWeb/actions';
import { curStateAccount, curStateRightWeb } from '@/Redux/selector';
import { hasAuthCookie } from '@/utils/auth';
import { useLoginModal } from '@/contexts/LoginModalContext';
import avatarDefault from "@assets/images/avatarDefault.svg";
import { Table, Text } from '@radix-ui/themes';
import { formatHistoryDate } from '../formatDate';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { NoDataContent } from './NoData';

const PAGE_SIZE = 13;
// Tab Turns > Weekly Reward: 10 item/trang cho vừa khung
const WEEKLY_PAGE_SIZE = 10;

function AvatarCell({ src, label }) {
	return (
		<div className="flex items-center h-full text-left gap-3">
			<img
				src={src?.split(",")?.[0] || avatarDefault}
				alt=""
				className="w-12 h-12 rounded-3xl object-cover"
				onError={(e) => { e.currentTarget.src = avatarDefault; }}
			/>
			<div className="max-w-[220px]">
				<Text className="font-medium text-[rgba(113,59,18,1)]">{label}</Text>
			</div>
		</div>
	);
}

function TimeCell({ value }) {
	return (
		<div className="flex items-center h-full text-left">
			<Text className="w-[70%] block font-medium text-[rgba(113,59,18,1)]">
				{formatHistoryDate(value)}
			</Text>
		</div>
	);
}

function PriceCell({ children }) {
	return (
		<div className="flex items-center h-full text-left">
			<Text className="font-medium text-[rgba(113,59,18,1)]">{children}</Text>
		</div>
	);
}

export default function AllCompete({ active, activeT }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const selectorRightWeb = useSelector(curStateRightWeb);
	const dispatch = useDispatch();
	const { openLogin } = useLoginModal();
	const account = useSelector(curStateAccount);
	const isAuthed = hasAuthCookie() || Boolean(account?.userInfo) || Boolean(account?.token);

	const isDaily = active === 'daily';

	const [idCurrentProduct, setIdCurrentProduct] = useState(selectorRightWeb.idCurrentProduct);
	const [sort, setSort] = useState('desc');
	const [page, setPage] = useState(1);
	const [dataResGuessesApi, setDataResGuessesApi] = useState(null);

	const prevTabRef = useRef({ active, activeT });

	useEffect(() => {
		if (selectorRightWeb.idCurrentProduct) {
			setIdCurrentProduct(selectorRightWeb.idCurrentProduct);
		}
	}, [selectorRightWeb.idCurrentProduct]);

	const afterGetProduct = (data) => {
		if (!data) return;
		const currentProduct = data.data.filter(
			(product) => new Date(product?.start_time).getTime() < currentDate
		);
		if (currentProduct?.[0]?.cp_id) {
			setIdCurrentProduct(currentProduct[0].cp_id);
		} else {
			dispatch(getHistoryBidAll({ query: { current: page, pageSize: WEEKLY_PAGE_SIZE, sort } }));
		}
	};

	const afterDailyPrizeGuesses = (data) => {
		if (data) setDataResGuessesApi(data);
	};

	// Gộp reset trang + fetch dữ liệu vào 1 effect duy nhất,
	useEffect(() => {
		const tabChanged =
			prevTabRef.current.active !== active || prevTabRef.current.activeT !== activeT;
		prevTabRef.current = { active, activeT };

		if (tabChanged && page !== 1) {
			setPage(1);
			return;
		}

		if (!isDaily) {
			if (idCurrentProduct) {
				dispatch(getHistoryBid({
					query: { current: page, pageSize: WEEKLY_PAGE_SIZE, sort, cp_ids: idCurrentProduct }
				}));
			} else {
				dispatch(getBidProduct({ callback: afterGetProduct }));
			}
		} else {
			dispatch(getDailyPrizeGuessesAll({
				query: { page, limit: PAGE_SIZE, scope: "all" },
				callback: afterDailyPrizeGuesses,
			}));
		}
	}, [idCurrentProduct, sort, page, active, activeT]);

	const weeklySource = idCurrentProduct ? selectorRightWeb?.bidHistory : selectorRightWeb?.bidHistoryAll;

	const weeklyHistory = useMemo(() => {
		return weeklySource?.data?.length > 0 ? weeklySource : null;
	}, [weeklySource]);

	const columns = useMemo(() => [
		{ key: "user", title: t('history.user') },
		{ key: "time", title: t('history.time') },
		{ key: "price", title: t("right_page.bid_price") },
	], [t]);

	const handleClickButtonNodata = () => {
		if (!isAuthed) {
			return openLogin();
		}
		navigate(isDaily ? PATH.DAILY_REWARD : PATH.HOME);
	};

	const view = isDaily
		? {
			rows: dataResGuessesApi?.data?.items || [],
			totalPages: Math.ceil((dataResGuessesApi?.data?.total || 0) / Number(dataResGuessesApi?.data?.limit || 1)),
			isEmpty: dataResGuessesApi?.data?.items?.length === 0,
			getUser: (item) => item.isdn_masked,
			getTime: (item) => item.created_at,
			getPrice: () => "*** MMK",
		}
		: {
			rows: weeklyHistory?.data || [],
			totalPages: Math.ceil((weeklyHistory?.total || 0) / Number(weeklyHistory?.pageSize || 1)),
			isEmpty: weeklyHistory?.data?.length === 0,
			getUser: (item) => item.isdn,
			getTime: (item) => item.auction_time,
			getPrice: (item) => `${item?.price?.toLocaleString("en-US")} MMK`,
		};

	if (view.isEmpty == null || view.isEmpty) {
		return (
			<NoDataContent
				title={t('history.no_guesses_yet')}
				textBtn={t('history.guess_price')}
				describe={activeT === 0
					? t('history.be_the_first_to_guess_the_price')
					: t('history.make_your_first_guess_to_join_the_challenge')}
				handleClick={handleClickButtonNodata}
			/>
		);
	}

	return (
		<Tables
			fill
			current={page}
			onChange={setPage}
			columns={columns}
			pagination={true}
			data={view.rows}
			totalPages={view.totalPages}
			renderRow={(item, index) => (
				<Table.Row key={item.id + index + 1}>
					<Table.Cell>
						<AvatarCell src={item.image} label={view.getUser(item)} />
					</Table.Cell>
					<Table.Cell>
						<TimeCell value={view.getTime(item)} />
					</Table.Cell>
					<Table.Cell>
						<PriceCell>{view.getPrice(item)}</PriceCell>
					</Table.Cell>
				</Table.Row>
			)}
		/>
	);
}